import { useState, useEffect } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "react-router";
import * as Sentry from "@sentry/react-router";

import "./app.css";

export const links = () => [];

export function Layout({ children }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

const isNetworkError = (error) =>
  error instanceof TypeError &&
  (error.message === "Failed to fetch" || error.message === "NetworkError when attempting to fetch resource." || error.message === "Load failed");

const NETWORK_ERROR_DELAY_MS = 5000;

export function ErrorBoundary() {
  const error = useRouteError();
  const [showError, setShowError] = useState(!isNetworkError(error));

  useEffect(() => {
    if (!isRouteErrorResponse(error) && !isNetworkError(error)) {
      Sentry.captureException(error);
    }
    if (isNetworkError(error)) {
      const timer = setTimeout(() => setShowError(true), NETWORK_ERROR_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Network error but still within the delay — show a loading state
  if (!showError) {
    return (
      <main style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "24px", textAlign: "center", color: "#1f2937" }}>
        <div style={{ width: "36px", height: "36px", border: "3px solid #e5e7eb", borderTopColor: "#6c47ff", borderRadius: "50%", animation: "spin 0.8s linear infinite", marginBottom: "16px" }} />
        <p style={{ fontSize: "14px", color: "#6b7280" }}>Loading...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </main>
    );
  }

  // Network error after delay — server genuinely unreachable
  if (isNetworkError(error)) {
    return (
      <main style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "24px", textAlign: "center", color: "#1f2937" }}>
        <div style={{ fontSize: "40px", marginBottom: "16px" }}>🔌</div>
        <h1 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}>Connection lost</h1>
        <p style={{ fontSize: "14px", color: "#6b7280", maxWidth: "320px", marginBottom: "24px" }}>
          We couldn&apos;t reach the Linkday server. Please check your connection and try again.
        </p>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: "8px 20px", fontSize: "14px", fontWeight: "500", borderRadius: "6px", border: "none", background: "#6c47ff", color: "#fff", cursor: "pointer" }}
          >
            Retry
          </button>
          <a
            href="mailto:support@linkday.app"
            style={{ padding: "8px 20px", fontSize: "14px", fontWeight: "500", borderRadius: "6px", border: "1px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer", textDecoration: "none" }}
          >
            Contact support
          </a>
        </div>
      </main>
    );
  }

  // Non-network errors — show immediately
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
